import { NativeEventSource, EventSourcePolyfill } from 'event-source-polyfill';

import { Logger } from 'utils/Logger';
import RouterService from 'utils/Router';

import { requestSseAccessToken } from 'reducers/sse/actions';

const logger = new Logger('SSE:Events');

export default (() => {
  const EventSource = NativeEventSource || EventSourcePolyfill;

  // private variables and methods scope
  const maxFailedStoreAccessTries = 3;
  const reconnectDefaultTimeoutSeconds = 3;
  const reconnectMaxTimeoutSeconds = 60;

  let failedStoreAccessCounter = 0;
  let reconnectTimeout = reconnectDefaultTimeoutSeconds;
  let store = null;
  let eventSource = null;

  const requestAccessToken = () =>
    new Promise((resolve, reject) => {
      store.dispatch(requestSseAccessToken(resolve, reject));
    });

  const reconnect = () => {
    const stopReconnecting = reconnectTimeout > reconnectMaxTimeoutSeconds;

    if (stopReconnecting) {
      logger.warn('Stop reconnecting.');
      return;
    }

    setTimeout(() => {
      SSEHandlerInstance.openConnection();
      reconnectTimeout += Math.sqrt(reconnectTimeout * 5);
    }, reconnectTimeout * 1000);
  };

  const onOpen = () => {
    reconnectTimeout = reconnectDefaultTimeoutSeconds;
  };

  const onMessage = (event) => {
    try {
      dispatchEvent(JSON.parse(event.data));
    } catch (e) {
      logger.warn(e);
    }
  };

  const onError = (event) => {
    logger.warn('Failed to receive SSE event.', event);
    SSEHandlerInstance.closeConnection();
    reconnect();
  };

  const dispatchEvent = ({ event, data }) => {
    if (!store) {
      return;
    }

    store.dispatch({
      type: `@@SSE/${event}`,
      payload: data,
      storeState: store.getState(),
    });
  };

  class SSEHandler {
    setStore(passedStore) {
      if (!store) {
        store = passedStore;
        failedStoreAccessCounter = 0;
      }
    }

    openConnection() {
      if (eventSource) {
        return;
      }

      // For some reason it is possible that login saga that call this method
      // is running before even redux store exist, so it will wait on that with 1s interval
      // most likely code inside the if statement will run 0 or 1 times
      if (!store && failedStoreAccessCounter < maxFailedStoreAccessTries) {
        failedStoreAccessCounter += 1;
        setTimeout(this.openConnection, 1000);
        return;
      }

      requestAccessToken()
        .then((token) => {
          logger.info(`Got new access token for SSE \`${token?.value}\``);

          eventSource = new EventSource(
            RouterService.generate('api.platform.events_stream', { token: token?.value }),
          );

          eventSource.onmessage = onMessage;
          eventSource.onerror = onError;
          eventSource.onopen = onOpen;
        })
        .catch(() => {
          onError();
          logger.warn('Failed to receive new access token for SSE');
        });
    }

    closeConnection() {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    }
  }

  const SSEHandlerInstance = Object.freeze(new SSEHandler());

  return SSEHandlerInstance;
})();
